import { useEffect, useMemo, useRef } from "react";

import { FieldValues, Path, PathValue, UseFormReturn } from "react-hook-form";

import { buildEventReminderFallback } from "../helpers/eventReminderFallback";

import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";

interface UseApplyReminderAIDefaultsParams<T extends FieldValues> {
  form: UseFormReturn<T>;
  showReminders: boolean;
  hasDate: boolean;
  eventName: string;
  isAIEligible: boolean;
  reminder1Exists: boolean;
  reminder2Exists: boolean;
}

export const useApplyReminderAIDefaults = <T extends FieldValues>({
  form,
  showReminders,
  hasDate,
  eventName,
  isAIEligible,
  reminder1Exists,
  reminder2Exists,
}: UseApplyReminderAIDefaultsParams<T>) => {
  const { t } = useTranslate();
  const { apiFormObject, isCreatedFromDuplicate } = useLiveFormEditorContext<"ticketing">();
  const { mutateAsync: getFormGeneratedByAIData } = trpc.getFormGeneratedByAIData.useMutation();

  const reminder1Defaults = useMemo(() => {
    const { subject, content } = buildEventReminderFallback({ reminderNumber: 1, eventName, hasDate, t });
    return { subject, content, offset: 7 };
  }, [eventName, hasDate, t]);
  const reminder2Defaults = useMemo(() => {
    const { subject, content } = buildEventReminderFallback({ reminderNumber: 2, eventName, hasDate, t });
    return { subject, content, offset: 1 };
  }, [eventName, hasDate, t]);

  const remindersDefaultsRef = useRef({ reminder1: reminder1Defaults, reminder2: reminder2Defaults });
  remindersDefaultsRef.current = { reminder1: reminder1Defaults, reminder2: reminder2Defaults };

  const hasAppliedRef = useRef(false);

  useEffect(() => {
    const canShowReminderCards = showReminders && hasDate;
    const shouldFetchReminder1 = canShowReminderCards && !reminder1Exists;
    const shouldFetchReminder2 = canShowReminderCards && !reminder2Exists;

    if (!isAIEligible || isCreatedFromDuplicate || (!shouldFetchReminder1 && !shouldFetchReminder2)) {
      return;
    }
    if (hasAppliedRef.current) {
      return;
    }
    hasAppliedRef.current = true;

    let mounted = true;

    const isReminderDirty = (key: "reminder1" | "reminder2") => {
      const dirty = (form.formState.dirtyFields as Record<string, Record<string, boolean | undefined> | undefined>)[
        key
      ];
      return Boolean(dirty?.subject || dirty?.content || dirty?.offset);
    };

    const setReminderValue = (path: string, value: unknown) => {
      form.setValue(path as Path<T>, value as PathValue<T, Path<T>>, { shouldDirty: true });
    };

    const applyReminder1Defaults = (subject: string, content: string) => {
      if (isReminderDirty("reminder1")) {
        return;
      }
      setReminderValue("reminder1.enabled", true);
      setReminderValue("reminder1.offset", remindersDefaultsRef.current.reminder1.offset);
      setReminderValue("reminder1.subject", subject);
      setReminderValue("reminder1.content", content);
    };

    const applyReminder2Defaults = (subject: string, content: string) => {
      if (isReminderDirty("reminder2")) {
        return;
      }
      setReminderValue("reminder2.enabled", true);
      setReminderValue("reminder2.offset", remindersDefaultsRef.current.reminder2.offset);
      setReminderValue("reminder2.subject", subject);
      setReminderValue("reminder2.content", content);
    };

    const fetchReminderDefaults = async () => {
      const response = await getFormGeneratedByAIData({ formId: apiFormObject.id });

      if (!mounted) {
        return;
      }

      if (shouldFetchReminder1) {
        const isReminder1Completed =
          response?.reminder1Status === "completed" && Boolean(response.reminder1Subject && response.reminder1Content);
        applyReminder1Defaults(
          isReminder1Completed ? response.reminder1Subject! : remindersDefaultsRef.current.reminder1.subject,
          isReminder1Completed ? response.reminder1Content! : remindersDefaultsRef.current.reminder1.content
        );
      }

      if (shouldFetchReminder2) {
        const isReminder2Completed =
          response?.reminder2Status === "completed" && Boolean(response.reminder2Subject && response.reminder2Content);
        applyReminder2Defaults(
          isReminder2Completed ? response.reminder2Subject! : remindersDefaultsRef.current.reminder2.subject,
          isReminder2Completed ? response.reminder2Content! : remindersDefaultsRef.current.reminder2.content
        );
      }
    };

    fetchReminderDefaults();

    return () => {
      mounted = false;
    };
  }, [
    isAIEligible,
    isCreatedFromDuplicate,
    apiFormObject.id,
    form,
    getFormGeneratedByAIData,
    hasDate,
    reminder1Exists,
    reminder2Exists,
    showReminders,
  ]);
};
